"use client";
import React, { useState, useEffect, useCallback } from "react";

import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import TopNavThree from "../../components/Header/TopNav/TopNavThree";
import MenuFurniture from "../../components/Header/Menu/MenuFurniture";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import Footer from "../../components/Footer/Footer";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { useCart, notifyCartStorageChanged } from "../../context/CartContext";
import {
  fetchProductImages,
  fetchProductText,
} from "../../data/Productlist";
import CartDebugger from "../../components/Other/CartDebugger";
import { CheckoutSteps } from "../../components/CheckoutSteps";
import { makeCartVariantKey } from "../../utils/cartVariantKey";
import { PriceDisplay } from "../../components/Other/PriceDisplay";

const Cart = () => {
  document.title = "Panier | Kissaria";
  const router = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Add navigation state tracking
  const [isNavigating, setIsNavigating] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  useEffect(() => {
    // Mark as initialized after component mounts
    setHasInitialized(true);
    
    // Handle browser back/forward navigation
    const handleBeforeUnload = () => {
      setIsNavigating(true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle location changes
  useEffect(() => {
    if (hasInitialized) {
      setIsNavigating(false);
      // Ensure proper scroll position
      window.scrollTo(0, 0);
    }
  }, [location.pathname, hasInitialized]);

  const { cartState, updateCart } = useCart();

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    // Tìm sản phẩm trong giỏ hàng
    const itemToUpdate = cartState.cartArray.find(
      (item) => item.id === productId
    );

    // Kiểm tra xem sản phẩm có tồn tại không
    if (itemToUpdate) {
      // Truyền giá trị hiện tại của selectedSize và selectedColor
      updateCart(
        productId,
        newQuantity,
        itemToUpdate.selectedSize,
        itemToUpdate.selectedColor
      );
    }
  };

  let moneyForFreeship = 750;
  let [totalCart, setTotalCart] = useState<number>(0);
  let [discountCart, setDiscountCart] = useState<number>(0);
  let [shipCart, setShipCart] = useState<number>(30);
  let [applyCode, setApplyCode] = useState<number>(0);

  if (totalCart < applyCode) {
    applyCode = 0;
    discountCart = 0;
  }

  if (totalCart < moneyForFreeship) {
    shipCart = 30;
  }

  if (cartState.cartArray.length === 0) {
    shipCart = 0;
  }

  const [dataSourceProd, setDataSourceProd] = useState<any[]>([]);
  const [isOpenProduct, setisOpenProduct] = useState(true);
  const [isLoadingText, setIsLoadingText] = useState(true);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  const fetchData = async () => {
    try {
      // Fetch text data first
      const products = await fetchProductText();

      // Set text data first (without images)
      setDataSourceProd(products);
      setisOpenProduct(false);
      setIsLoadingText(false); // Text data loaded

      // Fetch images for the products after the text data is loaded
      const productsWithImages = await fetchProductImages(products);

      // Set the updated products with images
      setDataSourceProd(productsWithImages);

      setIsLoadingImages(false); // Images data loaded
    } catch (error) {
      console.error("Failed to fetch products: ", error);
      // Ensure loading states are reset even on error
      setisOpenProduct(false);
      setIsLoadingText(false);
      setIsLoadingImages(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [cartDetails, setCartDetails] = useState<any[]>([]);
  const [colorImages, setColorImages] = useState<{[key: number | string]: string}>({});
  const hasSelectedVariantValue = (value: unknown) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "pas sélectionné" && str !== "0";
  };

  const fetchColorImage = async (imageid: number | string) => {
    if (!imageid || colorImages[imageid]) return; // Already loaded or no imageid
    
    // Use direct path instead of API call
    const imagePath = `/images/${imageid}`;
    setColorImages(prev => ({
      ...prev,
      [imageid]: imagePath
    }));
  };

  const updateCartDetails = useCallback(async () => {
    const cartItemsJSON = localStorage.getItem("cartItems");
    const cartItems: any[] = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];

    // Build cart lines from cartItems so same product can appear multiple times
    // with different color/size/pointure.
    const updatedCartDetails = cartItems
      .map((cartItem) => {
        const product = dataSourceProd.find(
          (p) => Number(p.id) === Number(cartItem.id)
        );
        if (!product) return null;

        const variantKey = makeCartVariantKey({
          productId: cartItem.id,
          colorCode: cartItem.colorCode,
          sizeCode: cartItem.sizeCode,
          pointureCode: cartItem.pointureCode,
        });

        return {
          ...product,
          variantKey,
          quantityPursh: cartItem.quantity ?? 1,
          sizePursh: cartItem.size ?? null,
          colorPursh: cartItem.color ?? null,
          colorCode: cartItem.colorCode ?? null,
          sizeCode: cartItem.sizeCode ?? null,
          pointureCode: cartItem.pointureCode ?? null,
          colorImageId: cartItem.colorImageId ?? null,
          pointurePursh: cartItem.pointure ?? "pas sélectionné",
          livraison: cartItem.livraison ?? null,
        };
      })
      .filter(Boolean) as any[];

    // Set the updated cart details state
    setCartDetails(updatedCartDetails);

    // Set color images directly from cart data (only for products with characteristics)
    for (const product of updatedCartDetails) {
      if (product.colorImageId && !product.colorImageId.includes('.') && !product.colorImageId.startsWith('/')) {
        // Only fetch if it's a numeric ID (not a filename like "59-2.jpg")
        fetchColorImage(product.colorImageId);
      }
    }
  }, [dataSourceProd]);

  const calculateTotalPrice = useCallback(() => {
    return cartDetails.reduce((total, product) => {
      const price = Number(product.price) || 0;
      const quantity = Number(product.quantityPursh) || 1;
      return total + (price * quantity);
    }, 0);
  }, [cartDetails]);

  // Update total whenever cartDetails changes
  useEffect(() => {
    const total = calculateTotalPrice();
    setTotalCart(total);
  }, [calculateTotalPrice]);

  // Update cart details when dataSourceProd changes
  useEffect(() => {
    if (dataSourceProd.length > 0) {
    updateCartDetails();
    }
  }, [dataSourceProd, updateCartDetails]);

  // Force recalculation when component mounts or when cart state changes
  useEffect(() => {
    const recalculateTotal = () => {
      const total = calculateTotalPrice();
      setTotalCart(total);
    };

    // Recalculate after a short delay to ensure all data is loaded
    const timeoutId = setTimeout(recalculateTotal, 100);
    
    return () => clearTimeout(timeoutId);
  }, [cartDetails, calculateTotalPrice]);

  const removeFromCartProd = (variantKey: string) => {
    // Remove from localStorage
    const cartItemsJSON = localStorage.getItem("cartItems");
    if (cartItemsJSON) {
      const cartItems = JSON.parse(cartItemsJSON);
      const updatedCartItems = cartItems.filter((item: any) => {
        const itemVariantKey = makeCartVariantKey({
          productId: item.id,
          colorCode: item.colorCode,
          sizeCode: item.sizeCode,
          pointureCode: item.pointureCode,
        });
        return itemVariantKey !== variantKey;
      });
      localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));
      notifyCartStorageChanged();
    }
    
    // Update cart details to reflect the change
    updateCartDetails();
  };

  const handleCheckout = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (cartDetails.length === 0) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    setIsCheckingOut(true);

    // Show loading state for 3 seconds
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // Navigate to checkout
    window.location.href = "/checkout";
  };

  // Show loading state if navigating or not initialized
  if (isNavigating || !hasInitialized) {
    return (
      <div className="fixed top-0 left-0 w-full h-screen flex justify-center items-center bg-white z-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink"></div>
      </div>
    );
  }

  return (
    <>
      <TopNavThree props="style-three bg-white" />
      <div id="header" className="relative w-full">
        <MenuFurniture props="" />
        <Breadcrumb heading="Panier" subHeading="Panier" />
      </div>
      <div className="cart-block md:py-20 py-10">
        <div className="container px-4 md:px-0">
          <CheckoutSteps currentStep={1} />
          <div className="content-main flex justify-between max-xl:flex-col gap-y-8">
          <div className="xl:w-2/3 xl:pr-3 w-full overflow-hidden">
  {/* Enhanced Free shipping banner */}
  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-4 mt-5 shadow-sm">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
          <Icon.Truck size={20} className="text-green-600" />
        </div>
        <div className="flex-1">
          {totalCart >= moneyForFreeship ? (
            <div className="text-green-700 font-semibold text-lg">
              🎉 Livraison gratuite obtenue !
            </div>
          ) : (
            <div className="text-gray-700">
              <span className="font-medium">Achetez </span>
              <span className="text-green-600 font-bold text-lg">
                MAD {Number(moneyForFreeship - totalCart).toFixed(2)}
        </span>
              <span className="font-medium"> de plus pour obtenir </span>
              <span className="text-green-600 font-semibold">livraison gratuite</span>
            </div>
          )}
        </div>
      </div>
      <div className="hidden md:block text-right">
        <div className="text-sm text-gray-500">Seuil: MAD {moneyForFreeship}</div>
        <div className="text-sm text-gray-500">Actuel: MAD {totalCart.toFixed(2)}</div>
      </div>
    </div>
    
    {/* Progress bar */}
    <div className="mt-4">
      <div className="flex justify-between text-xs text-gray-500 mb-2">
        <span>MAD 0</span>
        <span>MAD {moneyForFreeship}</span>
      </div>
      <div className="w-full bg-gray-300 rounded-full h-4 shadow-inner overflow-hidden">
      <div
          className="bg-gradient-to-r from-pink to-pink-600 h-4 rounded-full transition-all duration-500 ease-out shadow-sm"
        style={{
            width: `${Math.min((totalCart / moneyForFreeship) * 100, 100)}%`,
            minWidth: '4px'
        }}
      ></div>
      </div>
      <div className="text-xs text-gray-600 mt-2 text-center font-medium">
        {Math.round((totalCart / moneyForFreeship) * 100)}% du seuil atteint
      </div>
    </div>
  </div>

  {/* Cart table header */}
  <div className="list-product w-full sm:mt-7 mt-5">
    <div className="w-full">
      <div className="heading bg-surface bora-4 pt-4 pb-4 hidden md:block">
        <div className="flex">
          <div className="w-1/2">
            <div className="text-button text-center">Produits</div>
          </div>
          <div className="w-1/12">
            <div className="text-button text-center">Prix</div>
          </div>
          <div className="w-1/6">
            <div className="text-button text-center">Quantité</div>
          </div>
          <div className="w-1/6">
            <div className="text-button text-center">Prix Total</div>
          </div>
          <div className="w-1/12">
            <div className="text-button text-center"></div>
          </div>
        </div>
      </div>

      {/* Cart content */}
      <div className="list-product-main w-full mt-3">
        {cartDetails.length < 1 ? (
          <p className="text-button pt-3">Aucun produit dans le panier</p>
        ) : (
          <>
            {/* Desktop Table Layout */}
            <div className="hidden md:block">
              {cartDetails.map((product) => (
            <div
              className="item flex md:mt-7 md:pb-7 mt-5 pb-5 border-b border-line w-full"
              key={product.variantKey}
            >
              {/* Product image & name */}
              <div className="w-1/2">
                <div className="flex items-center gap-6">
                  <div className="bg-img md:w-[100px] w-20 aspect-[3/4]">
                    <img
                      src={
                        product.colorImageId 
                          ? (product.colorImageId.startsWith('/') ? product.colorImageId : `/images/${product.colorImageId}`)
                          : `/images/${product.id}.jpg`
                      }
                      width={1000}
                      height={1000}
                      alt={product.name}
                      onError={(e) => {
                        e.currentTarget.src = "/default.png"; 
                      }}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                  <div>
                    <div
                      className="text-title"
                      style={{
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {product.name}
                    </div>
                    {(hasSelectedVariantValue(product.colorPursh) ||
                      hasSelectedVariantValue(product.sizePursh) ||
                      hasSelectedVariantValue(product.pointurePursh)) && (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {hasSelectedVariantValue(product.sizePursh) && (
                          <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">
                            T: {product.sizePursh}
                          </span>
                        )}
                        {hasSelectedVariantValue(product.pointurePursh) && (
                          <span className="bg-cyan-100 text-cyan-700 text-xs px-2 py-1 rounded-full">
                            P: {product.pointurePursh}
                          </span>
                        )}
                        {hasSelectedVariantValue(product.colorPursh) && (
                          <span className="bg-purple-100 text-purple-600 text-xs px-2 py-1 rounded-full">
                            C: {product.colorPursh}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Price */}
              <div className="w-1/12 flex items-center justify-center">
                <div className="text-title text-center">
                  <PriceDisplay amount={product.price} size="sm" />
                </div>
              </div>
              {/* Quantity */}
              <div className="w-1/6 flex items-center justify-center">
                <div className="quantity-block bg-surface md:p-3 p-2 flex items-center justify-between rounded-lg border border-line md:w-[100px] w-20">
                  <div className="text-button quantity">
                    {product.quantityPursh}
                  </div>
                </div>
              </div>
              {/* Total price */}
              <div className="w-1/6 flex items-center justify-center">
                <div className="text-title text-center">
                  <PriceDisplay
                    amount={product.quantityPursh * product.price}
                    size="sm"
                  />
                </div>
              </div>
              {/* Remove button */}
              <div className="w-1/12 flex items-center justify-center">
                    <button
                  onClick={() => removeFromCartProd(product.variantKey)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Icon.Trash size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Mobile Card Layout */}
            <div className="md:hidden flex flex-col gap-4 w-full">
              {cartDetails.map((product) => (
                <div
                  key={product.variantKey}
                  className="bg-white rounded-xl shadow p-3 flex gap-3 items-start border border-line w-full min-w-0"
                >
                  <div className="w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={
                        product.colorImageId 
                          ? (product.colorImageId.startsWith('/') ? product.colorImageId : `/images/${product.colorImageId}`)
                          : `/images/${product.id}.jpg`
                      }
                      width={64}
                      height={80}
                      alt={product.name}
                      onError={(e) => {
                        e.currentTarget.src = "/default.png";
                      }}
                      className="w-full h-full object-cover"
                />
              </div>
                  <div className="flex-1 flex flex-col gap-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-800 line-clamp-2 break-words">{product.name}</div>
                    {(hasSelectedVariantValue(product.colorPursh) ||
                      hasSelectedVariantValue(product.sizePursh) ||
                      hasSelectedVariantValue(product.pointurePursh)) && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {hasSelectedVariantValue(product.sizePursh) && (
                          <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">
                            T: {product.sizePursh}
                          </span>
                        )}
                        {hasSelectedVariantValue(product.pointurePursh) && (
                          <span className="bg-cyan-100 text-cyan-700 text-xs px-2 py-1 rounded-full">
                            P: {product.pointurePursh}
                          </span>
                        )}
                        {hasSelectedVariantValue(product.colorPursh) && (
                          <span className="bg-purple-100 text-purple-600 text-xs px-2 py-1 rounded-full">
                            C: {product.colorPursh}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-pink font-bold text-sm">
                        <PriceDisplay
                          amount={product.price}
                          size="sm"
                          amountClassName="!text-pink"
                          currencyClassName="!text-pink/80"
                        />
                      </span>
                      <span className="text-xs text-gray-400">x{product.quantityPursh}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Total:{" "}
                      <span className="font-bold text-green-600 inline-flex">
                        <PriceDisplay
                          amount={product.quantityPursh * product.price}
                          size="sm"
                          amountClassName="!text-green-600"
                          currencyClassName="!text-green-600/90"
                        />
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => removeFromCartProd(product.variantKey)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full border border-red-200 bg-red-50 flex-shrink-0"
                        title="Supprimer"
                      >
                        <Icon.Trash size={16} />
                      </button>
                      <span className="text-xs text-gray-400">Supprimer</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  </div>
</div>

            <div className="xl:w-1/3 xl:pl-12 w-full">
              <div className="checkout-block bg-surface p-6 rounded-2xl">
                <div className="heading5">Résumé de la commande</div>
                
                <div className="total-block py-5 flex justify-between border-b border-line">
                  <div className="text-title">Sous-total</div>
                  <div className="text-title total-product">
                    <PriceDisplay amount={totalCart} size="md" />
                  </div>
                </div>
                
                <div className="ship-block py-5 flex justify-between border-b border-line">
                  <div className="text-title">Expédition</div>
                  <div className="choose-type flex gap-12">
                    <div className="right">
                      <div className="ship text-gray-500 text-sm">À choisir à l'étape suivante</div>
                    </div>
                  </div>
                </div>
                <div className="total-cart-block pt-4 pb-4 flex justify-between">
                  <div className="heading5">Total</div>
                  <div className="heading5 total-cart">
                    <PriceDisplay
                      amount={totalCart - discountCart}
                      size="lg"
                    />
                  </div>
                </div>
                <div className="block-button flex flex-col items-center gap-y-4 mt-5">
                  <a
                    className={`checkout-btn button-main text-center w-full flex items-center justify-center gap-3 py-4 px-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${
                      cartDetails.length === 0 
                        ? 'opacity-50 cursor-not-allowed bg-gray-400 hover:bg-gray-400' 
                        : 'bg-pink hover:bg-pink-600 text-white transform hover:scale-105 active:scale-95'
                    } ${isCheckingOut ? 'opacity-75 cursor-not-allowed' : ''}`}
                    href={cartDetails.length === 0 ? "#" : "/checkout"}
                    onClick={handleCheckout}
                  >
                    {isCheckingOut ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Préparation de la commande...
                      </>
                    ) : (
                      <>
                        <Icon.CreditCard size={24} />
                        {cartDetails.length === 0 ? 'Panier vide' : 'Passer à la caisse'}
                      </>
                    )}
                  </a>
                  <Link
                    to="/shop"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-pink bg-white px-4 py-3.5 text-sm font-semibold uppercase tracking-wide text-pink shadow-sm transition-all duration-200 hover:bg-pink hover:text-white hover:shadow-md active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-pink focus-visible:ring-offset-2"
                  >
                    <Icon.Storefront size={22} weight="duotone" className="shrink-0" />
                    <span>Ou continuer vos achats</span>
                    <Icon.ArrowRight size={18} weight="bold" className="shrink-0 opacity-90" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      
      {/* Temporary cart debugger - remove after testing */}
      {/* <CartDebugger 
        cartDetails={cartDetails}
        totalCart={totalCart}
        dataSourceProd={dataSourceProd}
      /> */}
    </>
  );
};

export default Cart;
